<template>
    <el-card class="displaysettings absolute top-right fs-3 zi-100">
        <div class="flex-level pb-3 bb-1">
            <h3 class="flex m-0">{{ $l10n.__('Gestion & ordre des colonnes') }}</h3>
            <a
                class="close-btn"
                @click.prevent="$store.commit(`${listinghash}/hideDisplay`, 'columns')"
            >
                &times;
            </a>
        </div>

        <p class="my-3 gray-60">
            {{
                $l10n.__(
                    "Modifiez l'ordre des labels visibles pour organiser votre tableau comme vous le désirez.",
                )
            }}
            {{
                $l10n.__(
                    'Gérez les informations visibles dans votre tableau en cochant les colonnes que vous souhaitez afficher.',
                )
            }}
        </p>

        <div class="flex-level flex-top columns-sortable bb-1">
            <!-- LEFT COLUMN -->
            <div class="flex pr-3 hp-10">
                <h4 class="mt-0">{{ $l10n.__('Colonnes visibles') }}</h4>

                <div v-if="!!activeColumns.length" class="column-list-container ovy-auto">
                    <div v-if="mainColumn" class="p-1 pl-3 flex-level">
                        <div class="flex">
                            {{ mainColumn.label }}
                        </div>
                    </div>
                    <SaasDraggable v-model="draggableColumns" dragClass="itm-column">
                        <template
                            v-for="column in draggableColumns"
                            :key="`displayed_${column.name}`"
                        >
                            <div
                                class="p-1 pl-3 flex-level"
                                :class="{ 'itm-column cursor-move': !column.isMain }"
                            >
                                <div class="flex">
                                    <badge
                                        v-if="column.name.startsWith('cf_')"
                                        dot
                                        inline
                                        tooltip
                                        :badges="getBadges(column)"
                                    >
                                        {{ column.label }}
                                    </badge>
                                    <span v-else>{{ column.label }}</span>
                                </div>

                                <a
                                    href="javascript:;"
                                    class="close-btn"
                                    @click="removeDisplay(column)"
                                >
                                    &times;
                                </a>
                            </div>
                        </template>
                    </SaasDraggable>
                </div>
            </div>

            <div class="flex pl-3 bl-1 hp-10">
                <h4 class="mt-0">{{ $l10n.__('Colonnes disponibles') }}</h4>

                <!-- COLUMN SEARCHBAR -->
                <div class="form-group my-2 prelative">
                    <div class="form-input wp-10">
                        <input
                            ref="searchcolumn"
                            v-model="searchword"
                            type="text"
                            class="h-4 wp-10 search_input"
                            :placeholder="$t.__('common.actions.search_column')"
                        />
                        <a
                            v-if="searchword != ''"
                            href="javascript:;"
                            class="close-btn search_clear"
                            @click="searchword = ''"
                            >&times;</a
                        >
                    </div>
                </div>

                <!-- LIST -->
                <ul class="columns_list">
                    <li
                        v-for="column in filteredColumns"
                        :key="`available_${column.name}`"
                        class="p-1 pl-0"
                    >
                        <label :for="column.name" class="flex-row">
                            <input
                                :id="column.name"
                                type="checkbox"
                                class="wauto"
                                :checked="column.display"
                                :disabled="column.isMain"
                                @change="toggleDisplay($event, column)"
                            />
                            <badge
                                v-if="column.name.startsWith('cf_')"
                                dot
                                inline
                                tooltip
                                :badges="getBadges(column)"
                            >
                                {{ column.label }}
                            </badge>
                            <span v-else>{{ column.label }}</span>
                        </label>
                    </li>
                </ul>
            </div>
        </div>

        <div class="displaysettings__footer">
            <CompassPlainButton
                :label="$t.__('common.actions.reset_columns')"
                variant="neutral"
                leftIcon="revert"
                @click.prevent="resetDisplay"
            />
            <CompassButton :label="$t.__('common.actions.apply')" @click="apply" />
        </div>
    </el-card>
</template>

<script>
import { removeAccents } from 'Core/commons/Utilities';
import Badge from 'vcomponents/utilities/Badge.vue';
import TagHumansConfig from 'Config/authenticated/TagHumans';
import SaasDraggable from 'vcomponents/common/SaasDraggable.vue';

export default {

    components: {
        SaasDraggable,
        Badge,
    },
    props: {
        listinghash: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            canApply: true,
            columns: [],
            originalDisplay: [],
            searchword: '',
        };
    },
    computed: {
        activeColumns: {
            get() {
                return this.columns
                    .filter((col) => col.display)
                    .sort((a, b) => {
                        return a.rank - b.rank;
                    });
            },
            set(columns) {
                this.updateColumnDisplay(columns);
            },
        },
        draggableColumns: {
            get() {
                return this.activeColumns.filter((col) => !col.isMain);
            },
            set(columns) {
                this.updateColumnDisplay(columns);
            },
        },
        filteredColumns() {
            if (this.searchword.trim() != '') {
                var regexp = new RegExp(removeAccents(this.searchword.trim()), 'i');

                return this.columns.filter((col) => {
                    return regexp.test(removeAccents(col.label));
                });
            }

            return this.columns;
        },

        mainColumn() {
            const mainIndex = _.findIndex(this.columns, { isMain: true });
            if (mainIndex != -1) {
                return this.columns[mainIndex];
            }

            return false;
        },
    },
    created() {
        this.initData();
    },

    methods: {
        toggleDisplay(evt, column) {
            const display = evt.target.checked;
            const index = _.findIndex(this.columns, column);

            if (index != -1) {
                this.columns[index].display = display;
            }
        },

        removeDisplay(column) {
            const index = _.findIndex(this.columns, column);
            if (index != -1) {
                this.columns[index].display = false;
            }
        },

        updateColumnDisplay(columns, display = true) {
            const mainIndex = _.findIndex(this.columns, { isMain: true });

            // Force main column at rank 0
            if (mainIndex != -1) {
                const mainCol = {
                    ...this.columns[mainIndex],
                };
                mainCol.display = true;
                mainCol.rank = 0;

                this.columns[mainIndex] = mainCol;
            }

            // Take care of other columns
            columns.forEach((col, rank) => {
                if (!col.isMain) {
                    const index = _.findIndex(this.columns, { name: col.name });

                    if (index != -1) {
                        col.rank = rank + 1;
                        this.columns[index] = col;
                        rank++;
                    }
                }
            });
        },

        apply() {
            // if no new columns > no need to fetch, just call server for columns update

            let needFetch = false;

            const activeKeys = this.activeColumns.map((col) => col.name);

            if (_.isEqual(activeKeys, this.originalDisplay)) {
                return;
            }

            for (const col of this.activeColumns) {
                const index = this.originalDisplay.indexOf(col.name);
                const wasAlreadyThere = index != -1;

                if (!wasAlreadyThere) {
                    needFetch = true;
                }
            }

            this.$store.commit(`${this.listinghash}/updateColumns`, this.columns);

            if (needFetch) {
                this.$store.dispatch(`${this.listinghash}/fetch`).then((response) => {
                    this.initData();
                });
            } else {
                this.$store
                    .dispatch(`${this.listinghash}/updateColumns`)
                    .then((response) => this.initData());
            }

            this.$store.commit(`${this.listinghash}/hideDisplay`, 'columns');
        },

        initData() {
            this.columns = JSON.parse(JSON.stringify(this.$store.state[this.listinghash].columns));

            this.originalDisplay = this.columns
                .filter((col) => col.display)
                .sort((a, b) => {
                    if (a.rank < b.rank) {
                        return -1;
                    } else if (a.rank > b.rank) {
                        return 1;
                    }
                    return 0;
                })
                .map((col) => col.name);
        },

        resetDisplay() {
            this.$store
                .dispatch(`${this.listinghash}/resetColumns`)
                .then((response) => {
                    this.columns = JSON.parse(
                        JSON.stringify(this.$store.state[this.listinghash].columns),
                    );
                })
                .catch((e) => {
                    null;
                });
        },

        /**
         * TODO: see if possible to refacto methode => also exist (FiltersPane.vue)
         * Filter on TagHumansConfig only tagHuman who match with relationTypes that use the custom field
         *
         * @param {{name: String}} {more} - prop of column, particulary "more" of column who contain "useOn[]"
         * @return {Object[]} Return an array of tagHumans
         */
        getBadges({ more }) {
            const relationTypes = more ? more.useOn : [];

            return Object.entries(TagHumansConfig)
                .map(([id, tag]) => ({ id, ...tag })) // instruction can be remove if we add directly id for each tag human
                .filter(({ id }) => relationTypes.includes(id));
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.displaysettings {
    width: 520px;
    z-index: 100;
    transition: 300ms;

    &__footer {
        align-items: center;
        display: flex;
        gap: var(--spacing-2);
        justify-content: flex-end;
        padding: var(--spacing-3) 0 0;
    }
}

.column-list-container {
    height: 280px;

    &.with-searchbar {
        height: 228px;
    }

    :not(:has(.saas-item--draggable.sortable-ghost)) {
        .itm-column:hover {
            background-color: $primary-05;

            &:before {
                font-family: 'icomoon' !important;
                content: '\e96c';
                // content     :"⋮⋮";
                position: absolute;
                top: 4px;
                left: 2px;
                cursor: pointer;
                color: $gray-60;
            }

            .close-btn {
                display: block;
            }
        }
    }
}

.itm-column {
    position: relative;
    border-radius: var(--spacing-0);
    .close-btn {
        display: none;
    }

    &.sortable-drag {
        background: $white;
        border: 1px solid $border-color;
        border-radius: var(--spacing-0);
        box-shadow: 0 0 var(--spacing-0) 0 rgba($gray-100, 0.2);
    }

    &.sortable-ghost {
        background-color: $lightgrey;
        text-indent: -1000;
    }
}

.columns_list {
    height: calc(280px - 36px - 8px);
    overflow-y: auto;
    margin: 0px;
}

.search_input {
    padding-right: 26px !important;
}

.search_clear {
    position: absolute;
    top: 10px;
    right: 8px;
}
</style>
